// src/components/Navigation.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const Nav = styled.nav`
  padding: 15px 0;
  margin-bottom: 20px;
  width: 100%;
  max-width: 1200px;
  position: relative;
`;

const NavList = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 15px 0;
  text-align: center;
  display: ${props => props.mobileView ? 'none' : 'flex'};
  flex-wrap: nowrap;
  justify-content: center;
  position: relative;
  gap: 5px;
  
  &::before, &::after {
    content: "";
    position: absolute;
    height: 1px;
    width: 100%;
    background: linear-gradient(to right, transparent, rgba(179, 139, 109, 0.4), transparent);
    left: 0;
  }
  
  &::before {
    top: 0;
  }
  
  &::after {
    bottom: 0;
  }
  
  ${props => props.mobileExpanded && `
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: rgba(255, 255, 255, 0.95);
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 100;
    box-shadow: 0 4px 10px rgba(0,0,0,0.1);
    border-radius: 0 0 8px 8px;
    animation: slideDown 0.3s ease;
    
    @keyframes slideDown {
      from { opacity: 0; transform: translateY(-10px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `}
`;

const NavItem = styled.li`
  margin: 0 8px;
  padding: 8px 0;
  position: relative;
  
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: ${props => props.active ? '100%' : '0'};
    height: 2px;
    background: linear-gradient(to right, #b38b6d, #d4b59e);
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }
  
  ${props => props.mobileExpanded && `
    margin: 8px 0;
    width: 100%;
    padding: 10px 0;
    
    &:not(:last-child) {
      border-bottom: 1px solid rgba(179, 139, 109, 0.2);
    }
  `}
`;

const NavLink = styled(Link)`
  color: #947060;
  text-decoration: none;
  font-family: 'Cormorant Garamond', serif;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  transition: all 0.3s ease;
  position: relative;
  padding: 5px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  
  &:hover {
    color: #b38b6d;
    transform: translateY(-2px);
  }
  
  ${props => props.mobileExpanded && `
    font-size: 1.2rem;
    padding: 10px;
  `}
`;

const NavDecoration = styled.span`
  display: inline-block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(179, 139, 109, 0.6);
  margin: 0 5px;
  position: relative;
  
  &::before, &::after {
    content: "";
    position: absolute;
    width: 10px;
    height: 1px;
    background: rgba(179, 139, 109, 0.3);
    top: 50%;
  }
  
  &::before {
    left: -12px;
  }
  
  &::after {
    right: -12px;
  }
  
  ${props => props.mobileExpanded && `
    display: none;
  `}
`;

const MobileMenuButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: #947060;
  font-size: 1.5rem;
  cursor: pointer;
  margin: 10px auto;
  padding: 5px 15px;
  border-radius: 4px;
  border: 1px solid rgba(179, 139, 109, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(179, 139, 109, 0.1);
  }
  
  ${props => props.mobileView && `
    display: block;
  `}
`;

const Navigation = () => {
  const location = useLocation();
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [mobileView, setMobileView] = useState(false);

  const toggleMobileMenu = () => {
    setMobileExpanded(!mobileExpanded);
  };
  
  const checkMobileView = () => {
    setMobileView(window.innerWidth <= 768);
  };
  
  useEffect(() => {
    // Initial check
    checkMobileView();
    
    // Add resize listener
    window.addEventListener('resize', checkMobileView);
    
    // Clean up
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);
  
  // Close mobile menu when route changes
  useEffect(() => {
    setMobileExpanded(false);
  }, [location]);
  
  const navItems = [
    { path: '/', label: 'Home' },
    // { path: '/save-the-date', label: 'Register - Save The Date' },
    { path: '/rsvp', label: 'RSVP' },
    { path: '/wedding-details', label: 'Wedding Details' },
    { path: '/registry', label: 'Registry' },
    { path: '/our-story', label: 'Our Story' },
    // { path: '/photos', label: 'Photos' },
    { path: '/liveodds', label: 'Polls' },
  ];

  return (
    <Nav>
      <MobileMenuButton 
        onClick={toggleMobileMenu} 
        mobileView={mobileView}
      >
        {mobileExpanded ? 'Close ✕' : 'Menu ☰'}
      </MobileMenuButton>
      
      <NavList 
        mobileView={mobileView}
        mobileExpanded={mobileExpanded}
      >
        {navItems.map((item, index) => (
          <React.Fragment key={item.path}>
            <NavItem 
              active={location.pathname === item.path}
              mobileExpanded={mobileExpanded}
            >
              <NavLink 
                to={item.path}
                mobileExpanded={mobileExpanded}
              >
                {item.label}
              </NavLink>
            </NavItem>
            {index < navItems.length - 1 && !mobileExpanded && (
              <NavDecoration mobileExpanded={mobileExpanded} />
            )}
          </React.Fragment>
        ))}
      </NavList>
    </Nav>
  );
};

export default Navigation;